import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdBolt, MdOutlineSettingsInputAntenna, MdOutlineCenterFocusWeak, MdOutlineCamera, MdOutlineAutorenew, MdOutlineLocalOffer } from 'react-icons/md';

export default function SamsungLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#1428a0"; 
  const secondary = T.secondaryColor || "#007cff";
  const text = p.storefrontText || {};
  
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSpecTab, setActiveSpecTab] = useState("camera");

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Welcome to the Future of Tech",
    title: "Innovation That Inspires.",
    subtitle: "Explore next-generation AI-powered smartphones, smart TVs, and ultra-performance computing solutions.",
    primaryCta: "Shop Tech",
    secondaryCta: "Learn More",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#040814] text-slate-100 antialiased" style={{ fontFamily: T.fontFamily || "Poppins, sans-serif" }}>
      {/* Top Banner with moving text feel */}
      <div className="w-full bg-gradient-to-r from-[#1428a0] to-[#007cff] py-2 px-4 text-center text-xs font-semibold tracking-wider text-white shadow-sm">
        <span className="inline-flex items-center gap-1.5"><MdBolt className="text-yellow-300 animate-pulse" /> FUTURE IS NOW: Get up to 15% cashback with direct store checkout!</span>
      </div>

      {/* Samsung Premium Dark Header */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#040814]/90 backdrop-blur-xl border-b border-slate-800 shadow-2xl" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-blue-500/30 shadow-[0_0_10px_rgba(0,124,255,0.2)]" size="h-9 w-9" />
            <div>
              <p className="text-base font-extrabold tracking-tight bg-gradient-to-r from-white to-blue-400 bg-clip-text text-transparent">
                <InlineEditableText p={p} field="businessName" value={p.storeInfo?.businessName}>
                  {p.storeInfo?.businessName || "Your Store"}
                </InlineEditableText>
              </p>
              <p className="text-[9px] text-blue-400 font-bold tracking-widest uppercase">{p.storeInfo?.businessCategory || "Premium Electronics"}</p>
            </div>
          </div>

          {/* Navigation links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop Now"],
              ["details", "Store Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-semibold tracking-widest uppercase transition-all hover:text-blue-400 ${
                  activePage === page 
                    ? "text-blue-400 border-b-2 border-blue-500 pb-1" 
                    : "text-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search, Wishlist, Cart */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="hidden md:block w-48" />
            
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-xl hover:bg-slate-900 border border-slate-800 text-slate-300 hover:text-red-400 transition-colors" title="Wishlist">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4.5 w-4.5 text-[8px] font-black rounded-full text-white flex items-center justify-center bg-blue-500 shadow-md">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} dark />
            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-xl border border-slate-800 text-slate-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Drawer */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#080d22] border-b border-slate-800 shadow-2xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop Now"],
              ["details", "Store Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-xs font-bold tracking-wider uppercase border-b border-slate-900 hover:text-blue-400 ${
                  activePage === page ? "text-blue-400" : "text-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Samsung Split Hero with glowing neon accents */}
            <section className="relative w-full max-w-6xl mx-auto px-6 pt-6 pb-12">
              <div className="relative rounded-3xl overflow-hidden bg-slate-950 border border-slate-800 shadow-2xl p-8 md:p-14 flex flex-col md:flex-row items-center gap-8 min-h-[460px]">
                {/* Neon Glow Effects */}
                <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-blue-600/20 blur-[120px] pointer-events-none" />
                <div className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full bg-cyan-500/25 blur-[120px] pointer-events-none" />

                {/* Left Side Content */}
                <div className="flex-1 text-left relative z-10">
                  <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-widest text-cyan-400 bg-cyan-950/40 px-3 py-1 rounded-full border border-cyan-800/40 mb-4">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-ping" />
                    <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                  </span>
                  <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-4 tracking-tight">
                    <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                  </h1>
                  <p className="text-slate-400 text-sm md:text-base leading-relaxed mb-8 max-w-lg">
                    <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => navigateTo("shop")}
                      className="px-7 py-3 rounded-xl text-xs font-bold uppercase tracking-wider text-white shadow-lg hover:shadow-cyan-500/10 hover:scale-102 transition-all"
                      style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
                    >
                      <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                    </button>
                    <button 
                      onClick={() => { p.setActiveTab("details"); navigateTo("about"); }}
                      className="px-7 py-3 rounded-xl text-xs font-bold uppercase tracking-wider text-slate-300 border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 transition-colors"
                    >
                      <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                    </button>
                  </div>
                </div>

                {/* Right Side Visual */}
                <div className="flex-1 w-full relative z-10 self-stretch rounded-2xl overflow-hidden border border-slate-900 shadow-inner min-h-[280px]">
                  <HeroImage p={p} themeId="samsung" className="absolute inset-0 w-full h-full">
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-slate-950/60 to-transparent pointer-events-none" />
                  </HeroImage>
                </div>
              </div>
            </section>


            {/* Samsung Core Value Badges */}
            <section className="bg-slate-950 border-y border-slate-800 py-10 px-6">
              <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "HyperFast Logistics", icon: <MdBolt className="text-2xl text-blue-400" />, desc: "Tracked express shipping in 24 hours" },
                  { title: "Flagship Support", icon: <MdOutlineSettingsInputAntenna className="text-2xl text-blue-400" />, desc: "24/7 dedicated brand tech support" },
                  { title: "Special Exchange Value", icon: <MdOutlineLocalOffer className="text-2xl text-blue-400" />, desc: "Save on your next device upgrade" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4 border border-slate-900 rounded-2xl bg-[#080d22]">
                    <div className="p-3 rounded-full bg-slate-950 mb-3 border border-slate-800">{b.icon}</div>
                    <p className="text-xs font-bold text-white">{b.title}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog Section */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-400">FLAGSHIP CATALOG</span>
            </div>

            {/* Filter and Tab Panel Toggle */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900/30 text-xs font-semibold text-slate-300 transition-colors hover:bg-slate-900"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-xl border border-slate-800 bg-slate-950 p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: secondary, color: "#fff" } : { color: "#94a3b8" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} dark />
          </div>
        </section>
      </main>

      <StoreFooter p={p} dark />
    </div>
  );
}
